




















































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { requiredIf } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

import { BusinessHoursViewModel } from '@/features/bots/models';
import DateTimeService from '@/shared/services/dateTime';
import { BusinessHoursOption, BusinessHoursOptions } from '.';

import { helpers } from 'vuelidate/lib/validators';
import { Getter } from 'vuex-class';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import { isSameDay, hasOverlappingOpenClose, hasOverlappingHours, hasAtLeastOneOnlineTime } from './validationHelpers';

const time = (value: string, vm: any) => {
	if (!vm.open) {
		return true;
	}
	return !value || DateTimeService.isValidTime(value);
};


const after = (prop: any) =>
	helpers.withParams({ type: 'after', prop }, (value: any, parentVm: any) => {
		if (!value) {
			return true; // Required validation will deal with this
		}
		const compareTo = helpers.ref(prop, parentVm, parentVm);
		const minutes = DateTimeService.getMinutes(value);
		const compareToMinutes = DateTimeService.getMinutes(compareTo);
		if (!compareToMinutes) {
			return true;
		}
		return minutes !== undefined && minutes > compareToMinutes;
	});


/**
 * A validation method which compares are the openingTimes and checks to see if any of them overlap.
 */
const hasNoOverlap = () => helpers.withParams({ type: 'hasNoOverlap'}, (value: any) => {
	for (let i = 0; i < value.length; i++) {
		for (let j = i + 1; j < value.length; j++) {
			const current = value[i];
			const comparison = value[j];
			if (!isSameDay(current, comparison)) {
				// If they're not the same day, they are valid.
				continue;
			}
			if (hasOverlappingOpenClose(current, comparison)) {
				return false;
			}
			if (!(current.startTime && comparison.endTime)) {
				// If these aren't defined, we can just ignore them
				continue;
			}
			if (hasOverlappingHours(current, comparison)) {
				return false;
			}
		}
	}
	return true;
});

const hasOnlineTimes = () => helpers.withParams({ type: 'hasOnlineTimes' }, (value: BusinessHoursOption[], parentVm: any) => {
	if (!parentVm.enforceOnlineDayOfWeek) {
		return true;
	}
	return hasAtLeastOneOnlineTime(value);
});

@Component({
	mixins: [validationMixin],
	validations: {
		openingTimes: {
			hasAtLeastOneOnlineTime: hasOnlineTimes(),
			hasNoOverlap: hasNoOverlap(),
			$each: {
				day: {},
				startTime: {
					required: requiredIf((openTime) => openTime.open),
					time
				},
				endTime: {
					required: requiredIf((openTime) => openTime.open),
					time,
					after: after('startTime'),
				},
			},
		},
	},
})
export default class BusinessHoursSelection extends Vue {
	@Prop() public value: BusinessHoursViewModel[];
	@Prop() public allowEmpty: boolean | undefined;
	@Prop({ default: false }) public enforceOnlineDayOfWeek: boolean;
	@Getter public holidays!: HolidayViewModel[];

	public openingTimes: BusinessHoursOption[] = [];
	public timezone: string = 'Europe/London';

	public overlapErrorMessage = 'Must not contain conflicting business hours for the same day';
	public atLeastOneOnlineTimeErrorMessage = 'Default Business Hours must be online for at least one day of the week';

	get options(): any[] {
		const options = [];
		options.push({ label: 'Select...', value: '', disabled: true });
		options.push(
			...BusinessHoursOptions.dayOptions.map((opt) => ({
				label: opt.name,
				value: opt,
			}))
		);
		options.push({
			label: '──────────',
			value: 'divider1',
			disabled: true,
		});
		options.push(
			...BusinessHoursOptions.dayRangeOptions.map((opt) => ({
				label: opt.name,
				value: opt,
			}))
		);
		if (this.holidays.length > 0) {
			options.push({
				label: '──────────',
				value: 'divider2',
				disabled: true,
			});
			options.push(
				...this.holidays.map((h) => ({
					label: h.name,
					key: h.id,
					value: { name: h.name, holidayId: h.id },
				}))
			);
		}
		return options;
	}

	get showDeleteIcon(): boolean {
		if (this.openingTimes.length > 1) {
			return true;
		}
		return this.allowEmpty || this.allowEmpty === undefined;
	}

	public mounted() {
		this.openingTimes = this.value.map((vm) => BusinessHoursOption.fromViewModel(vm));
	}

	@Watch('holidays', { immediate: true })
	public onHolidayChanged(holidays: HolidayViewModel[]) {
		this.openingTimes = this.value.map((vm) => BusinessHoursOption.fromViewModel(vm));
	}

	public addOfficeHours() {
		this.openingTimes.push(BusinessHoursOptions.getDefaultOption());
		this.onChange();
	}

	public deleteOfficeHours(index: number) {
		this.openingTimes.splice(index, 1);
		this.onChange();
	}

	public generateErrorMessage(v: any, field: string, label: string): string | null {
		if (v[field].$invalid) {
			if (!v[field].required) {
				return `${label} is required.`;
			}
			if (v[field].hasOwnProperty('after')) {
				if (v[field].required && !v[field].after) {
					return 'Must be after start time';
				}
			}
			return 'Must be in HH:mm format';
		}
		return null;
	}

	private onChange() {
		this.$emit('error', this.$v.$invalid);
		this.$emit('input', this.openingTimes.map((r) => r.toViewModel()));
	}
}
